import React from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  SelectChangeEvent,
  Menu,
  Button
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Image,
  TableChart,
  Undo,
  Redo,
  AutoAwesome,
  FormatListNumbered,
  FormatListBulleted,
  Help,
  InsertPageBreak,
  Save,
  ArrowDropDown,
  EmojiEmotions,
  FormatUnderlined,
  FormatStrikethrough,
  Subscript,
  Superscript,
  FormatColorText,
  FormatColorFill,
  Fullscreen,
  FullscreenExit,
  Code,
  Print
} from '@mui/icons-material';
import { FontStyle } from './SysEditor';

interface EditorToolbarProps {
  fontStyle: FontStyle;
  onBoldClick: () => void;
  onItalicClick: () => void;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignRight: () => void;
  onAlignJustify: () => void;
  onFontSizeChange: (size: number) => void;
  onFontFamilyChange: (family: string) => void;
  onParagraphStyleChange: (style: string) => void;
  onOrderedListClick: (listType?: string) => void;
  onUnorderedListClick: () => void;
  onImageClick: () => void;
  onTableClick: () => void;
  onPageBreakClick: () => void;
  onUndoClick: () => void;
  onRedoClick: () => void;
  onAiClick: () => void;
  onHelpClick: () => void;
  onSaveClick?: () => void;
  onPrintClick?: () => void;
  onEmojiClick: (emoji: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  currentParagraphStyle: string;
  currentListType: string;
  disabled?: boolean;
  showSave?: boolean;
  onUnderlineClick?: () => void;
  onStrikethroughClick?: () => void;
  onSubscriptClick?: () => void;
  onSuperscriptClick?: () => void;
  onFontColorChange?: (color: string) => void;
  onBackgroundColorChange?: (color: string) => void;
  onFullscreenClick?: () => void;
  onSourceCodeClick?: () => void;
  isFullscreen?: boolean;
  isSourceCode?: boolean;
  paperSize?: string;
  onPaperSizeChange?: (size: string) => void;
}

const fontFamilies = [
  'Arial',
  'Arial Black',
  'Bookman',
  'Comic Sans MS',
  'Courier New',
  'Garamond',
  'Georgia',
  'Helvetica',
  'Impact',
  'Inter',
  'Lato',
  'Montserrat',
  'Monotype Corsiva',
  'Open Sans',
  'Palatino',
  'Roboto',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana'
];

const fontSizes = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 28, 36, 48, 72];

const paragraphStyles = [
  { value: 'p', label: 'Parágrafo Normal', tag: 'p' },
  { value: 'div', label: 'Parágrafo Normal (Div)', tag: 'div' },
  { value: 'h1', label: 'Título 1', tag: 'h1' },
  { value: 'h2', label: 'Título 2', tag: 'h2' },
  { value: 'h3', label: 'Título 3', tag: 'h3' },
  { value: 'h4', label: 'Título 4', tag: 'h4' },
  { value: 'h5', label: 'Título 5', tag: 'h5' },
  { value: 'h6', label: 'Título 6', tag: 'h6' },
  { value: 'blockquote', label: 'Citação', tag: 'blockquote' },
  { value: 'pre', label: 'Código', tag: 'pre' }
];

const paperSizes = [
  { value: 'auto', label: 'Livre / Auto' },
  { value: 'a4', label: 'A4 (210mm)' },
  { value: 'a5', label: 'A5 (148mm)' },
  { value: 'cupom80', label: 'Cupom 80mm' },
  { value: 'cupom57', label: 'Cupom 57mm' }
];

const listTypes = [
  { value: 'decimal', label: '1, 2, 3...', cssValue: 'decimal' },
  { value: 'lower-alpha', label: 'a, b, c...', cssValue: 'lower-alpha' },
  { value: 'upper-alpha', label: 'A, B, C...', cssValue: 'upper-alpha' },
  { value: 'lower-roman', label: 'i, ii, iii...', cssValue: 'lower-roman' },
  { value: 'upper-roman', label: 'I, II, III...', cssValue: 'upper-roman' }
];

const emojiCategories = {
  'Rostos e Pessoas': [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
    '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
    '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔',
    '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
    '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧'
  ],
  'Animais e Natureza': [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
    '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🐣',
    '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛',
    '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍'
  ],
  'Comida e Bebida': [
    '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒',
    '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬',
    '🥒', '🌶️', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🍞',
    '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩'
  ],
  'Atividades': [
    '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
    '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳',
    '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷', '⛸️',
    '🥌', '🎿', '⛷️', '🏂', '🪂', '🏋️', '🤼', '🤸', '⛹️', '🤺'
  ],
  'Objetos': [
    '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️',
    '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥',
    '📽️', '🎞️', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️',
    '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋'
  ],
  'Símbolos': [
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
    '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐'
  ]
};

const compactMenuProps = {
  sx: { zIndex: 10000 },
  PaperProps: {
    sx: { '& .MuiMenuItem-root': { fontSize: '0.78rem', minHeight: 30, py: 0.3 } },
  },
};

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  fontStyle,
  onBoldClick,
  onItalicClick,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onAlignJustify,
  onFontSizeChange,
  onFontFamilyChange,
  onParagraphStyleChange,
  onOrderedListClick,
  onUnorderedListClick,
  onImageClick,
  onTableClick,
  onPageBreakClick,
  onUndoClick,
  onRedoClick,
  onAiClick,
  onHelpClick,
  onSaveClick,
  onPrintClick,
  onEmojiClick,
  canUndo,
  canRedo,
  currentParagraphStyle,
  currentListType,
  disabled = false,
  showSave = false,
  onUnderlineClick,
  onStrikethroughClick,
  onSubscriptClick,
  onSuperscriptClick,
  onFontColorChange,
  onBackgroundColorChange,
  onFullscreenClick,
  onSourceCodeClick,
  isFullscreen = false,
  isSourceCode = false,
  paperSize = 'auto',
  onPaperSizeChange
}) => {
  const [listMenuAnchor, setListMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [emojiMenuAnchor, setEmojiMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedEmojiCategory, setSelectedEmojiCategory] = React.useState<string>('Rostos e Pessoas');
  const isListMenuOpen = Boolean(listMenuAnchor);
  const isEmojiMenuOpen = Boolean(emojiMenuAnchor);
  const handleFontFamilyChange = (event: SelectChangeEvent<string>) => {
    onFontFamilyChange(event.target.value);
  };

  const handleFontSizeChange = (event: SelectChangeEvent<string>) => {
    onFontSizeChange(parseInt(event.target.value));
  };

  const handleParagraphStyleChange = (event: SelectChangeEvent<string>) => {
    onParagraphStyleChange(event.target.value);
  };

  const handlePaperSizeChange = (event: SelectChangeEvent<string>) => {
    if (onPaperSizeChange) {
      onPaperSizeChange(event.target.value);
    }
  };

  const handleListMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setListMenuAnchor(event.currentTarget);
  };

  const handleListMenuClose = () => {
    setListMenuAnchor(null);
  };

  const handleListTypeSelect = (listType: string) => {
    onOrderedListClick(listType);
    handleListMenuClose();
  };

  const handleEmojiMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmojiMenuAnchor(event.currentTarget);
  };

  const handleEmojiMenuClose = () => {
    setEmojiMenuAnchor(null);
  };

  const handleEmojiSelect = (emoji: string) => {
    onEmojiClick(emoji);
    handleEmojiMenuClose();
  };

  const handleEmojiCategoryChange = (category: string) => {
    setSelectedEmojiCategory(category);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.4,
        px: 0.4,
        py: 0.8,
        bgcolor: 'action.hover',
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
        '& .MuiSvgIcon-root': { fontSize: '18px' },
        '& .MuiIconButton-sizeSmall': { p: '4px' },
        '& .MuiDivider-root': { mx: 0.3 },
        '& .MuiInputBase-root': { fontSize: '0.82rem', height: 30 },
        '& .MuiInputLabel-root': { fontSize: '0.79rem' },
        '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
      }}
    >
      {/* Salvar */}
      {showSave && (
        <>
          <Tooltip title="Salvar Conteúdo">
            <span>
              <IconButton
                onClick={onSaveClick}
                disabled={disabled}
                size="small"
                sx={{
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  }
                }}
              >
                <Save />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Seletor de Fonte */}
      <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel>Fonte</InputLabel>
        <Select
          value={fontStyle.fontFamily}
          label="Fonte"
          onChange={handleFontFamilyChange}
          disabled={disabled}
          MenuProps={compactMenuProps}
        >
          {fontFamilies.map((font) => (
            <MenuItem key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Seletor de Tamanho */}
      <FormControl size="small" sx={{ width: 68 }}>
        <InputLabel>Tamanho</InputLabel>
        <Select
          value={fontStyle.fontSize.toString()}
          label="Tamanho"
          onChange={handleFontSizeChange}
          disabled={disabled}
          MenuProps={compactMenuProps}
        >
          {fontSizes.map((size) => (
            <MenuItem key={size} value={size.toString()}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Seletor de Estilo de Parágrafo */}
      <FormControl size="small" sx={{ width: 140 }}>
        <InputLabel>Estilo</InputLabel>
        <Select
          value={currentParagraphStyle}
          label="Estilo"
          onChange={handleParagraphStyleChange}
          disabled={disabled}
          MenuProps={compactMenuProps}
        >
          {paragraphStyles.map((style) => (
            <MenuItem key={style.value} value={style.value}>
              <Box sx={{ margin: 0, fontSize: 'inherit', fontWeight: 'inherit' }}>
                {style.label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Seletor de Papel / Limite do Editor */}
      {onPaperSizeChange && (
        <FormControl size="small" sx={{ width: 120 }}>
          <InputLabel>Papel</InputLabel>
          <Select
            value={paperSize}
            label="Papel"
            onChange={handlePaperSizeChange}
            disabled={disabled}
            MenuProps={compactMenuProps}
          >
            {paperSizes.map((size) => (
              <MenuItem key={size.value} value={size.value}>
                <Box sx={{ margin: 0, fontSize: 'inherit', fontWeight: 'inherit' }}>
                  {size.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {onPaperSizeChange && <Divider orientation="vertical" flexItem />}

      {/* Desfazer/Refazer */}
      <Tooltip title="Desfazer (Ctrl+Z)">
        <span>
          <IconButton
            onClick={onUndoClick}
            disabled={disabled || !canUndo}
            size="small"
          >
            <Undo />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Refazer (Ctrl+Y)">
        <span>
          <IconButton
            onClick={onRedoClick}
            disabled={disabled || !canRedo}
            size="small"
          >
            <Redo />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Formatação de Texto */}
      <Tooltip title="Negrito">
        <span>
          <IconButton
            onClick={onBoldClick}
            disabled={disabled}
            color={fontStyle.bold ? 'primary' : 'default'}
            size="small"
          >
            <FormatBold />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Itálico">
        <span>
          <IconButton
            onClick={onItalicClick}
            disabled={disabled}
            color={fontStyle.italic ? 'primary' : 'default'}
            size="small"
          >
            <FormatItalic />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Sublinhado">
        <span>
          <IconButton
            onClick={onUnderlineClick}
            disabled={disabled}
            color={fontStyle.underline ? 'primary' : 'default'}
            size="small"
          >
            <FormatUnderlined />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Tachado">
        <span>
          <IconButton
            onClick={onStrikethroughClick}
            disabled={disabled}
            color={fontStyle.strikethrough ? 'primary' : 'default'}
            size="small"
          >
            <FormatStrikethrough />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Subscrito">
        <span>
          <IconButton
            onClick={onSubscriptClick}
            disabled={disabled}
            color={fontStyle.subscript ? 'primary' : 'default'}
            size="small"
          >
            <Subscript />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Sobrescrito">
        <span>
          <IconButton
            onClick={onSuperscriptClick}
            disabled={disabled}
            color={fontStyle.superscript ? 'primary' : 'default'}
            size="small"
          >
            <Superscript />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Cores */}
      <Tooltip title="Cor da Fonte">
        <span>
          <IconButton component="label" disabled={disabled} size="small">
            <FormatColorText style={{ color: fontStyle.color || 'inherit' }} />
            <input
              type="color"
              hidden
              value={fontStyle.color || '#000000'}
              onChange={(e) => onFontColorChange?.(e.target.value)}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Cor do Fundo">
        <span>
          <IconButton component="label" disabled={disabled} size="small">
            <FormatColorFill style={{ color: fontStyle.backgroundColor || 'inherit' }} />
            <input
              type="color"
              hidden
              value={fontStyle.backgroundColor || '#ffffff'}
              onChange={(e) => onBackgroundColorChange?.(e.target.value)}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Alinhamento */}
      <Tooltip title="Alinhar à esquerda">
        <span>
          <IconButton
            onClick={onAlignLeft}
            disabled={disabled}
            size="small"
          >
            <FormatAlignLeft />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Centralizar">
        <span>
          <IconButton
            onClick={onAlignCenter}
            disabled={disabled}
            size="small"
          >
            <FormatAlignCenter />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Alinhar à direita">
        <span>
          <IconButton
            onClick={onAlignRight}
            disabled={disabled}
            size="small"
          >
            <FormatAlignRight />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Justificar">
        <span>
          <IconButton
            onClick={onAlignJustify}
            disabled={disabled}
            size="small"
          >
            <FormatAlignJustify />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Listas */}
      <Tooltip title="Lista Numerada - Clique para escolher o tipo">
        <span>
          <Button
            onClick={handleListMenuClick}
            disabled={disabled}
            size="small"
            variant="text"
            sx={{
              minWidth: 'auto',
              padding: '2px 4px',
              color: isListMenuOpen ? '#1976d2' : 'inherit',
              backgroundColor: isListMenuOpen ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
              },
              display: 'flex',
              alignItems: 'center',
              gap: 0,
            }}
            endIcon={<ArrowDropDown sx={{ fontSize: '14px !important' }} />}
          >
            <FormatListNumbered sx={{ fontSize: '17px' }} />
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={listMenuAnchor}
        open={isListMenuOpen}
        onClose={handleListMenuClose}
        sx={{ zIndex: 10000 }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {listTypes.map((type) => (
          <MenuItem
            key={type.value}
            onClick={() => handleListTypeSelect(type.value)}
            selected={currentListType === type.value}
            sx={{
              fontSize: '14px',
              padding: '8px 16px',
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                color: '#1976d2',
                fontWeight: 500
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              <Box sx={{ color: '#888', fontSize: '13px' }}>
                {type.label}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Tooltip title="Lista com Marcadores">
        <span>
          <IconButton
            onClick={onUnorderedListClick}
            disabled={disabled}
            size="small"
          >
            <FormatListBulleted />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Inserir Elementos */}
      <Tooltip title="Inserir Imagem">
        <span>
          <IconButton
            onClick={onImageClick}
            disabled={disabled}
            size="small"
          >
            <Image />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Inserir Tabela">
        <span>
          <IconButton
            onClick={onTableClick}
            disabled={disabled}
            size="small"
          >
            <TableChart />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Inserir Quebra de Página">
        <span>
          <IconButton
            onClick={onPageBreakClick}
            disabled={disabled}
            size="small"
          >
            <InsertPageBreak />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Menu de Emoji */}
      <Tooltip title="Inserir Emoji">
        <span>
          <IconButton
            onClick={handleEmojiMenuClick}
            disabled={disabled}
            size="small"
            sx={{
              backgroundColor: isEmojiMenuOpen ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
              },
              fontSize: '15px',
            }}
          >
            😀
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={emojiMenuAnchor}
        open={isEmojiMenuOpen}
        onClose={handleEmojiMenuClose}
        sx={{ zIndex: 10000 }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 400,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {/* Abas de Categorias */}
        <Box sx={{
          display: 'flex',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          overflowX: 'auto'
        }}>
          {Object.keys(emojiCategories).map((category) => (
            <Button
              key={category}
              onClick={() => handleEmojiCategoryChange(category)}
              size="small"
              sx={{
                minWidth: 'auto',
                padding: '8px 12px',
                fontSize: '11px',
                fontWeight: selectedEmojiCategory === category ? 600 : 400,
                color: selectedEmojiCategory === category ? '#ff9800' : '#666',
                backgroundColor: selectedEmojiCategory === category ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
                borderRadius: 0,
                borderBottom: selectedEmojiCategory === category ? '2px solid #ff9800' : '2px solid transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.05)',
                },
                whiteSpace: 'nowrap'
              }}
            >
              {category}
            </Button>
          ))}
        </Box>

        {/* Grid de Emojis */}
        <Box sx={{
          padding: 2,
          maxHeight: 300,
          overflowY: 'auto'
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 1
          }}>
            {emojiCategories[selectedEmojiCategory as keyof typeof emojiCategories].map((emoji, index) => (
              <Button
                key={`${emoji}-${index}`}
                onClick={() => handleEmojiSelect(emoji)}
                sx={{
                  minWidth: 'auto',
                  width: 36,
                  height: 36,
                  padding: 0,
                  fontSize: '20px',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {emoji}
              </Button>
            ))}
          </Box>
        </Box>
      </Menu>

      {/* IA */}
      <Tooltip title="Gerar Texto com IA">
        <span>
          <IconButton
            onClick={onAiClick}
            disabled={disabled}
            size="small"
            color="secondary"
          >
            <AutoAwesome />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Modo Fonte e Tela Cheia */}
      <Tooltip title={isSourceCode ? "Visualizar" : "Código Fonte"}>
        <span>
          <IconButton
            onClick={onSourceCodeClick}
            disabled={disabled}
            size="small"
            color={isSourceCode ? 'primary' : 'default'}
          >
            <Code />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={isFullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}>
        <span>
          <IconButton
            onClick={onFullscreenClick}
            disabled={disabled}
            size="small"
          >
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </span>
      </Tooltip>

      {/* Imprimir */}
      <Tooltip title="Imprimir Conteúdo">
        <span>
          <IconButton
            onClick={onPrintClick}
            disabled={disabled}
            size="small"
            color="primary"
          >
            <Print />
          </IconButton>
        </span>
      </Tooltip>

      {/* Ajuda */}
      <Tooltip title="Ajuda e Informações do Editor">
        <span>
          <IconButton
            onClick={onHelpClick}
            disabled={disabled}
            size="small"
            sx={{
              color: '#059669',
              '&:hover': {
                backgroundColor: 'rgba(5, 150, 105, 0.1)',
              }
            }}
          >
            <Help />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default React.memo(EditorToolbar); 